import { reaction } from 'mobx'
import { types, flow, getRoot, getParent, getSnapshot } from 'mobx-state-tree'
import Router from 'next/router'

import io from 'socket.io-client'
import buildCustomValidationStore from '~/stores/models/customValidation'

import { VA_REGIONS } from '~/components/traffic/lookup/Forms/Elements/CourtSelector'
import * as Delays from '~/utilities/delays'
import objectIsEmpty from '~/utilities/objectIsEmpty'
import * as LocalStorage from '~/utilities/localStorage'
import { getLanding } from '~/utilities/landingPage'
import {
  loadFlowState,
  saveFlowState,
  clearFlowState,
} from '~/utilities/trafficLookupFlowPersistence'
import * as StringTools from '~/utilities/string'
import { userInputFormatDate } from '~/utilities/dates'
import { chargeTrafficTicket, overwritePaymentProcessor } from '~/services/payments'
import { trackMetaBrowserPurchase } from '~/utilities/metaCapiBrowserEvents'
import * as TrafficTicketService from '~/services/trafficTicket'
import { unsubscribeByFetcherSearchContactInfo } from '~/services/fetcher'
import TRACKING from '~/services/tracking'
import * as TrafficStateService from '~/services/trafficState'
import TrafficState from '~/stores/trafficTicket/models/TrafficState'
import TrafficTicket from '~/stores/trafficTicket/models/TrafficTicket'

import {
  REQUEST_STATUSES,
  TRAFFIC_STATUS_ORDER,
  TRAFFIC_STATUS,
  FETCHER_NAMES_TITLES,
  FETCHER_CITY_STATES,
  STATES,
  getTrafficFetcherTimeout,
  PAYMENT_PROVIDERS,
  IS_FETCHER_COMBINED,
} from '~/utilities/constants'
import {
  SEARCH_OPTIONS,
  SEARCH_OPTIONS_STATES,
  SEARCH_OPTIONS_VALIDATION_KEYS,
  USER_INFO_SEARCH_OPTIONS,
  SEARCH_OPTIONS_STATES_IS_NYS_LICENSE,
} from '~/components/traffic/lookup/Forms/Elements/searchConfig'
import {
  SEARCH_FORM_KEYS,
  TRAFFIC_TICKET_KEYS_TO_SAVE,
  NYDMV_SUBMIT_KEYS_NY,
  NYDMV_SUBMIT_KEYS_NON_NY,
  NYDMV_PAYLOAD_KEYS,
  getUserSessionToken,
  FETCHER_VALIDATIONS,
  FETCHER_ASYNC_VALIDATIONS,
} from './lookupConfig'
import FetcherTicket from './models/FetcherTicket'
import { lookupPath } from '~/utilities/trafficLookupRoutes'

const TrafficLookupStore = types
  .model('TrafficLookupStore', {
    manualTrafficTicket: types.maybeNull(TrafficTicket),
    manualTrafficTicketError: types.optional(types.string, ''),
    isSubmittingManualTicket: types.optional(types.boolean, false),
    storeName: 'lookupStore',
    // license state (default NY for NY lookup form)
    licenseState: types.frozen({
      shortName: 'NY',
      longName: 'New York',
      available: true,
      prospect: true,
    }),
    trafficStates: types.array(TrafficState),
    fetcherState: types.optional(types.string, ''),
    fetcherName: types.optional(types.string, ''),
    isNYSLicense: types.optional(types.boolean, true),
    searchUsingOtherInfo: types.optional(types.boolean, false),
    searchUsingOtherInfoTouched: types.optional(types.boolean, false),
    /** Nassau: 'plate' | 'license' - which search tab is active */
    nassauSearchTab: types.optional(types.enumeration(['plate', 'license']), 'license'),
    // two search column configs
    columnOne: types.optional(
      types.enumeration(Object.keys(SEARCH_OPTIONS)),
      SEARCH_OPTIONS.NYSLicense,
    ),
    columnTwo: types.optional(
      types.enumeration(Object.keys(SEARCH_OPTIONS)),
      SEARCH_OPTIONS.lastFourSSN,
    ),
    columnThree: types.maybeNull(
      types.enumeration(Object.keys(SEARCH_OPTIONS)),
    ),
    // form inputs
    licenseID: '',
    licenseBack: '',
    plateNumber: '',
    SSNNumber1: '',
    SSNNumber2: '',
    SSNNumber3: '',
    SSNNumber4: '',
    zipCode: '',
    gender: 'male',
    DOBString: '',
    firstName: '',
    middleName: '',
    lastName: '',
    trafficTicketNumber: '',
    violateDate: '',
    // initialized: false,
    // statusText: '',
    canProgress: true,
    fetcherTickets: types.array(FetcherTicket),
    fetchingTicketsStatus: types.optional(types.string, REQUEST_STATUSES.init),
    selectedTrafficTicket: types.maybeNull(types.reference(FetcherTicket)),
    // Docket (v2) lookup only: every ticket checked for one checkout. The
    // first one mirrors into selectedTrafficTicket so the single-ticket
    // guards, validation and retainer link keep working.
    selectedTrafficTickets: types.array(types.safeReference(FetcherTicket)),
    fullName: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
    phoneNumber: types.optional(types.string, ''),
    source: types.optional(types.string, ''),
    popupIsOpen: types.optional(types.boolean, false),
    isStateAffiliatedWithWinitLaw: types.optional(types.boolean, false),
    useNYDMVForm: types.optional(types.boolean, false),
    /** Raw scan result from license scan when successful (optional, not required) */
    licenseRaw: types.maybeNull(types.frozen()),
    hasSentFetcherInfoAdded: types.optional(types.boolean, false),
    courtFilter: types.optional(types.string, ''),
    // Not part of persistableSlice on purpose — must reset to false on every
    // fresh store instance so results.jsx knows whether a restore attempt
    // has already run this mount (see restoreFlowState below).
    hasAttemptedRestore: types.optional(types.boolean, false),
  })
  .views((self) => ({
    // US state used to split GA4 funnels/conversions by jurisdiction.
    // Prefer the driver-license state, fall back to the fetcher/ticket state.
    get ga4State() {
      return (
        (self.licenseState && self.licenseState.shortName) ||
        self.fetcherState ||
        undefined
      )
    },
    get searchData() {
      const response = {}
      // For NYDMV form send as much data as we have (both NY and non-NY fields)
      const payloadKeys = self.useNYDMVForm ? NYDMV_PAYLOAD_KEYS : self.searchFormKeys
      payloadKeys.forEach((key) => {
        switch (key) {
          case 'SSN':
            response.last4SSN = self.SSN
            break
          case 'courtFilter':
            // Validation-only key; sent to the backend as `courts` below.
            break
          default:
            response[key] = self[key]
            break
        }
      })
      if (self.licenseRaw != null) {
        response.licenseRaw = self.licenseRaw
      }
      // Always send ticket & violate date for NYDMV (incl. NY license) so backend saves them to FetcherSearchCriteria
      if (self.useNYDMVForm) {
        response.trafficTicketNumber = self.trafficTicketNumber
        response.violateDate = self.violateDate
      }
      response.contactInfo = { ...self.contactInfo, ...getLanding() }
      // Only a non-default locale is a real signal; on the default (en) site the backend
      // falls back to the browser's Accept-Language. Close flags Spanish-speaking leads.
      if (Router.locale && Router.locale !== Router.defaultLocale) {
        response.contactInfo.lang = Router.locale
      }
      response.state = self.fetcherState
      response.fetcherName = self.fetcherName

      // Nassau: tell backend which search method (plate vs license)
      if (self.fetcherName === 'NY_NASSAU') {
        response.nassauSearchMethod = self.nassauSearchTab
        // Derive lastName from fullName for license search
        if (self.nassauSearchTab === 'license' && self.fullName) {
          const parts = self.fullName.trim().split(/\s+/)
          response.lastName = parts.length ? parts[parts.length - 1] : ''
        }
      }


      // VA: expand the selected region to its courts. A region is mandatory —
      // an unscoped search walks all 134 General District Courts and can never
      // finish inside the fetcher timeout.
      if (self.fetcherName === 'VA' && self.courtFilter) {
        response.courts = VA_REGIONS[self.courtFilter] || [self.courtFilter]
      }

      // If we have utm parameters from an anonymous user (not logged in), set the utm in contactInfo from AuthStore
      // Otherwise, set the utm object from the user. If the user is logged in and has arrived with UTM params,
      // they will be available on auth.user.utm (see handleUtmParameters() in _app.jsx).
      const { auth } = getRoot(self)
      // Meta last-touch: session fbclid alone (resolveAttribution clears session gclid on
      // a new Meta click). Do not resurrect User.gclid / User.utm onto a Meta visit —
      // that would stamp Close with GCLID + Source=meta.
      const metaLastTouch = Boolean(auth.fbclid) && !auth.gclid && !auth.msclkid
      if (metaLastTouch) {
        response.contactInfo.utm = !objectIsEmpty(auth.utmParameters)
          ? auth.utmParameters
          : {}
      } else if (!objectIsEmpty(auth.utmParameters)) {
        response.contactInfo.utm = auth.utmParameters
      } else {
        response.contactInfo.utm = auth.user.utm
      }
      const gclid = metaLastTouch ? undefined : auth.gclid || auth.user.gclid
      if (gclid) {
        response.contactInfo.gclid = gclid
        // Also put it on the search itself. contactInfo only becomes a row once the
        // visitor fills the contact step; a license/DOB-only lookup (~7% of web
        // searches) never gets there, so without this the click was lost. The backend
        // stores it on FetcherSearchCriteria (see saveSearchCriteria's field map).
        response.gclid = gclid
        if (auth.gclidCapturedAt) response.gclidCapturedAt = auth.gclidCapturedAt
      }
      const fbclid = metaLastTouch ? auth.fbclid : undefined
      if (fbclid) {
        response.contactInfo.fbclid = fbclid
        // Close LKN also reads fbclid off the utm blob (utmFieldsFromObject).
        // Always overwrite — same as gclid: session attribution is the freshest click.
        // Persist inferred source on FSCI (Close mergeCloseAttribution also invents).
        const existingUtm =
          response.contactInfo.utm && typeof response.contactInfo.utm === 'object'
            ? response.contactInfo.utm
            : {}
        response.contactInfo.utm = {
          ...existingUtm,
          fbclid,
          ...(!existingUtm.utm_source ? { utm_source: 'meta' } : {}),
        }
      }

      return response
    },
    get renderAttorneySelector() {
      if (
        (self.selectedTrafficTicket && self.selectedTrafficTicket.quotes.length <= 1) ||
        self.selectedTrafficTickets.length > 1 ||
        self.isStateAffiliatedWithWinitLaw
      ) {
        return false
      }
      return true
    },
    get SSN() {
      return self.SSNNumber1 + self.SSNNumber2 + self.SSNNumber3 + self.SSNNumber4
    },
    get totalPrice() {
      if (self.selectedTrafficTicket.fetcherTrafficTicket.quote.pricing) {
        return self.selectedTrafficTicket.fetcherTrafficTicket.quote.pricing.winItPrice
      }
      return 0
    },
    get allUniqueFAQs() {
      if (self.fetcherTickets && self.fetcherTickets.length) {
        const seenQuestions = {}
        return self.fetcherTickets
          .reduce((accum, item) => {
            const currentFAQ = item.allQuoteFAQs
            return accum.concat(currentFAQ)
          }, [])
          .filter((item) => {
            if (seenQuestions[item[0]]) {
              return false
            }
            seenQuestions[item[0]] = true
            return true
          })
      }
      return []
    },
    get searchFormKeys() {
      if (self.useNYDMVForm && self.isNYSLicense) {
        return NYDMV_SUBMIT_KEYS_NY
      }
      if (self.useNYDMVForm && !self.isNYSLicense) {
        return NYDMV_SUBMIT_KEYS_NON_NY
      }
      if (self.useNYDMVForm) {
        return SEARCH_FORM_KEYS
      }
      // Nassau: use license tab keys when that tab is active (no userInfo - not shown for Nassau)
      if (self.fetcherName === 'NY_NASSAU' && self.nassauSearchTab === 'license') {
        return SEARCH_OPTIONS_VALIDATION_KEYS[SEARCH_OPTIONS.NassauLicenseId]
      }
      const columnOne = SEARCH_OPTIONS_VALIDATION_KEYS[self.columnOne]
      const columnTwo = SEARCH_OPTIONS_VALIDATION_KEYS[self.columnTwo]
      const columnThree = self.columnThree ? SEARCH_OPTIONS_VALIDATION_KEYS[self.columnThree] ?? [] : []
      let userInputFields = Object.keys(USER_INFO_SEARCH_OPTIONS)
      if (self.columnOne === 'nameDOBGender' || self.columnOne === 'Name' || self.columnThree === 'Name') {
        userInputFields = userInputFields.filter((val) => val !== 'fullName')
      }
      const regionField = self.fetcherName === 'VA' ? ['courtFilter'] : []
      return [...columnOne, ...columnTwo, ...columnThree, ...userInputFields, ...regionField]
    },
    get searchFormHasErrors() {
      return self.validateKeys(self.searchFormKeys)
    },
    get fightableTickets() {
      return self.fetcherTickets.filter((ticket) => ticket.isFightable)
    },
    get checkoutTickets() {
      if (self.selectedTrafficTickets.length) return self.selectedTrafficTickets.slice()
      return self.selectedTrafficTicket ? [self.selectedTrafficTicket] : []
    },
    get checkoutTotalPrice() {
      return self.checkoutTickets.reduce(
        (total, ticket) => total + ticket.fetcherTrafficTicket.totalPrice,
        0,
      )
    },
    get contactInfo() {
      if (self.useNYDMVForm) {
        return {
          fullName: self.fullName,
          email: self.email,
          phoneNumber: self.phoneNumber,
        }
      }
      if (self.columnOne === 'nameDOBGender' || self.columnOne === 'Name' || self.columnThree === 'Name') {
        return {
          fullName: `${self.firstName} ${
            self.middleName ? `${self.middleName} ` : ''
          }${self.lastName}`.trim(),
          email: self.email,
          phoneNumber: self.phoneNumber,
        }
      }
      return {
        fullName: self.fullName,
        email: self.email,
        phoneNumber: self.phoneNumber,
      }
    },
    allowCreateManualTrafficTicket() {
      const ticket = self.manualTrafficTicket
      if (ticket.ticketID.length === 0 && ticket.validateKey('persistedTicketImage')) {
        return false
      }
      if (ticket.validateKey('ticketID')) {
        return false
      }
      return true
    },
    getLookupFormSnapshot() {
      return {
        fullName: self.fullName,
        email: self.email,
        phoneNumber: self.phoneNumber,
        licenseID: self.licenseID,
        DOBString: self.DOBString,
        zipCode: self.zipCode,
        trafficTicketNumber: self.trafficTicketNumber,
        violateDate: self.violateDate,
        licenseBack: self.licenseBack,
        gender: self.gender,
        licenseStateShortName: self.licenseState?.shortName,
        ssn: self.SSN,
        licenseRaw: self.licenseRaw,
        courtFilter: self.courtFilter,
      }
    },
    // Minimal, serializable slice needed to rebuild results/attorney/checkout
    // after a refresh. Full snapshot per ticket — fightability, status, and
    // quotes are needed as-is by the results/attorney/checkout pages, so a
    // hand-picked subset would leave restored tickets mis-rendered.
    // View (not action) so the autosave reaction tracks its dependencies.
    get persistableSlice() {
      return {
        fetcherTickets: self.fetcherTickets.map((ticket) => getSnapshot(ticket)),
        selectedTicketId: self.selectedTrafficTicket ? self.selectedTrafficTicket.ticketID : null,
        selectedTicketIds: self.selectedTrafficTickets.map((ticket) => ticket.ticketID),
        fetcherName: self.fetcherName,
        fetcherState: self.fetcherState,
        isNYSLicense: self.isNYSLicense,
        useNYDMVForm: self.useNYDMVForm,
        columnOne: self.columnOne,
        columnTwo: self.columnTwo,
        columnThree: self.columnThree,
        nassauSearchTab: self.nassauSearchTab,
        courtFilter: self.courtFilter,
        source: self.source,
        fetchingTicketsStatus: self.fetchingTicketsStatus,
        isStateAffiliatedWithWinitLaw: self.isStateAffiliatedWithWinitLaw,
        hasSentFetcherInfoAdded: self.hasSentFetcherInfoAdded,
      }
    },
  }))
  .actions((self) => ({
    afterCreate() {
      // Todo: this needs a better name - currently it's far too abstract.
      self.manualTrafficTicket = TrafficTicket.create()
      if (process.browser) {
        reaction(
          () => JSON.stringify(self.getLookupFormSnapshot()),
          () => self.persistLookupFormToStorage(),
          { delay: 500 }
        )
        // Persist the flow slice to sessionStorage whenever relevant state
        // changes (debounced 500ms), so results/attorney/checkout/success can
        // rebuild themselves after a hard refresh instead of bouncing the
        // user back to the first lookup page. Mirrors the parking flow
        // (utilities/flowPersistence.js).
        reaction(
          () => JSON.stringify(self.persistableSlice),
          () => saveFlowState(self.persistableSlice),
          { delay: 500 }
        )
      }
    },
    // Rebuild the lookup flow from a saved slice. Synchronous (tickets are
    // restored from stored snapshots, no network). Returns false if there is
    // nothing to restore so the caller (page guard) can bounce to the start
    // of the lookup flow instead.
    restoreFlowState() {
      if (!process.browser) return false
      // Set before the lookup below (and regardless of outcome) so the
      // results page can tell "haven't checked sessionStorage yet" (still
      // show a loading state) apart from "checked, there's genuinely
      // nothing" (show the no-tickets empty state).
      self.hasAttemptedRestore = true
      const slice = loadFlowState()
      if (!slice || !Array.isArray(slice.fetcherTickets) || !slice.fetcherTickets.length) {
        return false
      }
      self.fetcherTickets = slice.fetcherTickets
      if (slice.fetcherName) self.fetcherName = slice.fetcherName
      if (slice.fetcherState) self.fetcherState = slice.fetcherState
      self.isNYSLicense = !!slice.isNYSLicense
      self.useNYDMVForm = !!slice.useNYDMVForm
      if (slice.columnOne) self.columnOne = slice.columnOne
      if (slice.columnTwo) self.columnTwo = slice.columnTwo
      self.columnThree = slice.columnThree || null
      self.nassauSearchTab = slice.nassauSearchTab || 'license'
      self.courtFilter = slice.courtFilter || ''
      self.source = slice.source || ''
      self.isStateAffiliatedWithWinitLaw = !!slice.isStateAffiliatedWithWinitLaw
      self.hasSentFetcherInfoAdded = !!slice.hasSentFetcherInfoAdded
      if (slice.fetchingTicketsStatus) self.fetchingTicketsStatus = slice.fetchingTicketsStatus
      if (slice.selectedTicketId) {
        const match = self.fetcherTickets.find((ticket) => ticket.ticketID === slice.selectedTicketId)
        // Assign directly rather than via setSelectedTrafficTicket(), which
        // force-selects quotes[0] — that would silently revert an attorney
        // the user already picked (fetcherTrafficTicket.quote is restored
        // as-is from the snapshot, so it already reflects their choice).
        if (match) self.selectedTrafficTicket = match
      }
      if (Array.isArray(slice.selectedTicketIds)) {
        self.selectedTrafficTickets = slice.selectedTicketIds.filter((ticketID) =>
          self.fetcherTickets.some((ticket) => ticket.ticketID === ticketID),
        )
      }
      return true
    },
    // Called once the flow reaches a terminal step (success / manual
    // submission) so a refresh there does not rehydrate a completed
    // submission. The autosave reaction stays alive for the next search.
    clearFlowSession() {
      if (!process.browser) return
      clearFlowState()
    },
    prepopulateUserInfo() {
      const { user } = getRoot(self).auth
      this.setEmail(user.email)
      this.setFullName(user.name)
      this.setPhoneNumber(user.phoneNumber)
      this.setLicenseID(user.licenseID)
      if (process.browser && (!user.name || !user.email || !user.phoneNumber || !user.licenseID)) {
        const saved = LocalStorage.get('trafficLookupForm', true)
        if (saved) {
          if (!user.name && saved.fullName) this.setFullName(saved.fullName)
          if (!user.email && saved.email) this.setEmail(saved.email)
          if (!user.phoneNumber && saved.phoneNumber) this.setPhoneNumber(saved.phoneNumber)
          if (!user.licenseID && saved.licenseID) this.setLicenseID(saved.licenseID)
        }
      }
    },
    persistLookupFormToStorage() {
      if (!process.browser) return
      try {
        LocalStorage.set('trafficLookupForm', JSON.stringify(self.getLookupFormSnapshot()))
      } catch (_) {}
    },
    restoreLookupFormFromStorage() {
      if (!process.browser) return
      try {
        const saved = LocalStorage.get('trafficLookupForm', true)
        if (!saved || typeof saved !== 'object') return
        if (saved.fullName != null) self.fullName = String(saved.fullName)
        if (saved.email != null) self.email = String(saved.email)
        if (saved.phoneNumber != null) self.phoneNumber = String(saved.phoneNumber)
        if (saved.licenseID != null) self.licenseID = String(saved.licenseID)
        if (saved.DOBString != null) self.DOBString = String(saved.DOBString)
        if (saved.zipCode != null) self.zipCode = String(saved.zipCode).replace(/\D/g, '').slice(0, 5)
        if (saved.trafficTicketNumber != null) self.trafficTicketNumber = String(saved.trafficTicketNumber).toUpperCase()
        if (saved.violateDate != null) self.violateDate = String(saved.violateDate)
        if (saved.licenseBack != null) self.licenseBack = String(saved.licenseBack).toUpperCase()
        if (saved.gender === 'female' || saved.gender === 'male') self.gender = saved.gender
        if (saved.ssn != null) {
          const str = String(saved.ssn).replace(/\D/g, '').slice(0, 4)
          if (str.length === 4) {
            self.SSNNumber1 = str[0]
            self.SSNNumber2 = str[1]
            self.SSNNumber3 = str[2]
            self.SSNNumber4 = str[3]
          }
        }
        if (saved.licenseStateShortName && self.trafficStates && self.trafficStates.length > 0) {
          self.setLicenseState({ value: saved.licenseStateShortName })
        }
        if (saved.licenseRaw != null && typeof saved.licenseRaw === 'object') {
          self.licenseRaw = saved.licenseRaw
        }
        // Region is required for VA, so leaving it out of the restore would
        // block submit on a form that otherwise looks fully repopulated.
        if (saved.courtFilter != null) self.courtFilter = String(saved.courtFilter)
      } catch (_) {}
    },
    setEmail(email) {
      self.email = email
    },
    setFullName(fullName) {
      self.fullName = fullName
    },
    setPhoneNumber(phoneNumber) {
      self.phoneNumber = phoneNumber
    },
    setLicenseID(licenseID) {
      self.licenseID = licenseID
    },
    openPopup() {
      self.popupIsOpen = true
    },
    closePopup() {
      self.popupIsOpen = false
    },
    setLicenseState(newStateOption) {
      const target = self.trafficStates.filter((item) => item.shortName === newStateOption.value)
      if (!target || !target.length) return false
      self.licenseState = {
        shortName: target[0].shortName,
        longName: target[0].longName,
        available: target[0].available,
        prospect: target[0].prospect,
      }
      self.isNYSLicense = self.licenseState.shortName === 'NY'
      // Form columns are always determined by the fetcher, not by the selected license state
      const fetcherConfig = SEARCH_OPTIONS_STATES[self.fetcherName]
      if (fetcherConfig) {
        self.columnOne = fetcherConfig.columnOne
        self.columnTwo = fetcherConfig.columnTwo
        self.columnThree = fetcherConfig.columnThree ?? null
      } else {
        self.columnOne = SEARCH_OPTIONS.nameDOBGender
        self.columnTwo = SEARCH_OPTIONS.ticketNumber
        self.columnThree = null
      }
      return self.licenseState
    },
    loadTrafficStates: flow(function* fetchStates() {
      try {
        const trafficStates = yield TrafficStateService.getAll()
        self.setTrafficStates(trafficStates)
      } catch (error) {
        console.error('Error fetching the states.')
      }
    }),
    checkStateAffiliatedWithWinitLaw(stateShortName) {
      try {
        switch (stateShortName) {
          case 'CAL':
          case 'CAL_STATE':
          case 'CAL_SANTA_BARBARA':
          case 'CAL_ORANGE':
          case 'CAL_SAN_FRANCISCO':
          case 'CAL_VENTURA':
          case 'CAL_SAN_BERNARDINO':
          case 'CAL_SAN_DIEGO':
          case 'CAL_SANTA_CLARA':
          case 'CAL_KERN':
            stateShortName = 'CA'
            break
          case 'GA':
            stateShortName = 'GA'
            break
          case 'FL_MIAMI':
            stateShortName = 'FL'
            break
          case 'WA_NEW_SEATTLE':
            stateShortName = 'WA'
            break
          case 'PA_PHILADELPHIA':
            stateShortName = 'PA'
            break
          case 'NY_NASSAU':
          case 'NY_SUFFOLK':
          case 'NY_WESTCHESTER':
            stateShortName = 'NY'
            break
          case 'NY_CRIMINAL':
          case 'NY_STATE':
            stateShortName = 'NY'
            break
          case 'TX_AUSTIN':
            stateShortName = 'TX'
            break
          case 'CAL_SAN_JOAQUIN':
            stateShortName = 'CA'
            break
          default:
            break
        }

        const trafficState = self.trafficStates.filter((item) => item.shortName === stateShortName)
        if (!trafficState || !trafficState.length) return false

        return trafficState[0].affiliatedWithWinitLaw
      } catch (error) {
        console.error('Error fetching affiliatedWithWinitLaw flag for state', stateShortName)
      }
    },
    setTrafficStates(statesArray) {
      self.trafficStates = statesArray
    },
    setIsStateAffiliatedWithWinitLaw(stateShortName) {
      switch (stateShortName) {
        case 'CAL':
        case 'CAL_STATE':
        case 'CAL_SANTA_BARBARA':
        case 'CAL_ORANGE':
        case 'CAL_SAN_FRANCISCO':
        case 'CAL_VENTURA':
        case 'CAL_SAN_BERNARDINO':
        case 'CAL_SAN_DIEGO':
        case 'CAL_SANTA_CLARA':
        case 'CAL_KERN':
          stateShortName = 'CA'
          break
        case 'GA':
          stateShortName = 'GA'
          break
        case 'FL_MIAMI':
          stateShortName = 'FL'
          break
        case 'WA_NEW_SEATTLE':
          stateShortName = 'WA'
          break
        case 'PA_PHILADELPHIA':
          stateShortName = 'PA'
          break
        case 'NY_NASSAU':
        case 'NY_SUFFOLK':
        case 'NY_WESTCHESTER':
          stateShortName = 'NY'
          break
        case 'NY_CRIMINAL':
          stateShortName = 'NY'
          break
        case 'TX_AUSTIN':
          stateShortName = 'TX'
          break
        case 'CAL_SAN_JOAQUIN':
          stateShortName = 'CA'
          break
        default:
          break
      }
      try {
        const trafficState = self.trafficStates.filter((item) => item.shortName === stateShortName)
        if (!trafficState) console.error('Error fetching traffic state for ', stateShortName)
        self.isStateAffiliatedWithWinitLaw = trafficState[0].affiliatedWithWinitLaw
      } catch (error) {
        console.error('Error fetching affiliatedWithWinitLaw flag for state', stateShortName)
        self.isStateAffiliatedWithWinitLaw = false
      }
    },
    /**
     * For NY DMV multi-step form: fire a lightweight socket.io call with the current
     * search criteria so the backend can persist / observe partial data.
     * This does NOT listen for results and does NOT trigger any tracking conversions.
     */
    savePartialSearchCriteria(stepLabel) {
      if (!process.browser) return
      try {
        const url = process.env.PARSE_SERV_URL
        const socket = io(url, {
          transports: ['websocket'],
        })
        const searchCriteria = {
          ...self.searchData,
          partial: true,
          telemetry: {
            origin_create: 'v2 web app traffic lookup flow – partial save',
            created_source: 'regular_flow',
            lookup_page: 'traffic-lookup',
            step: stepLabel,
            fetcher_name: self.fetcherName,
          },
        }
        socket.on('connect', () => {
          socket.emit('getFetcherTickets', searchCriteria)
          // We don't care about results; close shortly after emit to avoid leaks.
          setTimeout(() => {
            try {
              socket.close()
            } catch (_) {}
          }, 5000)
        })
      } catch (error) {
        console.error('Error saving partial NY DMV search criteria', error)
      }
    },
    validateForm: flow(function* (keys) {
      self.resetTouched()
      self.resetDisplayErrors()

      const keysToValidate = keys || self.searchFormKeys
      self.validateKeyList(keysToValidate)
      yield self.asyncValidateKeyList(keysToValidate)
    }),
    // FORM INPUTS
    changeLicenseIDNumber(newLicenseIdNumber) {
      if (
        !newLicenseIdNumber.length ||
        (newLicenseIdNumber.length && /^[a-zA-Z0-9]*$/.test(newLicenseIdNumber))
      ) {
        self.addTouched('licenseID')
        self.licenseID = newLicenseIdNumber.toUpperCase()
      }
    },
    changeLicenseBack(value) {
      self.addTouched('licenseBack')
      self.licenseBack = String(value ?? '').toUpperCase()
    },
    setLicenseRaw(raw) {
      if (raw == null) {
        self.licenseRaw = null
        return
      }
      if (typeof raw !== 'object') {
        self.licenseRaw = raw
        return
      }
      const omit = [
        'pdf417',
        'middleNameTruncation',
        'firstNameTruncation',
        'lastNameTruncation',
        'hairColor',
        'version',
      ]
      const copy = { ...raw }
      omit.forEach((k) => delete copy[k])
      self.licenseRaw = copy
    },
    markFetcherInfoAdded() {
      self.hasSentFetcherInfoAdded = true
    },
    changeZipCode(newZipCode) {
      self.addTouched('zipCode')
      self.zipCode = newZipCode
    },
    changePlateNumber(newPlateNumber) {
      if (
        !newPlateNumber.length ||
        (newPlateNumber.length && /^[a-zA-Z0-9]*$/.test(newPlateNumber))
      ) {
        self.addTouched('plateNumber')
        self.plateNumber = newPlateNumber.toUpperCase()
      }
    },
    setFetcherName(fetcherName) {
      self.fetcherName = fetcherName
      if (fetcherName !== 'NY_NASSAU') {
        self.nassauSearchTab = 'license'
      }
      self.isNYSLicense = SEARCH_OPTIONS_STATES_IS_NYS_LICENSE[self.fetcherName]
      self.setColumn('columnOne', SEARCH_OPTIONS_STATES[self.fetcherName].columnOne)
      self.setColumn('columnTwo', SEARCH_OPTIONS_STATES[self.fetcherName].columnTwo)
      if (SEARCH_OPTIONS_STATES[self.fetcherName].columnThree) {
        self.setColumn('columnThree', SEARCH_OPTIONS_STATES[self.fetcherName].columnThree)
      } else {
        self.columnThree = null
      }
    },
    setFetcherState(fetcherState) {
      self.fetcherState = fetcherState
    },
    addLast4SSN(name, newValue) {
      // TODO: refactor to make SSN one string
      self.addTouched('SSN')
      self[name] = newValue
    },
    deleteLast4SSN(name) {
      // TODO: refactor to make SSN one string
      self.addTouched('SSN')
      self[name] = ''
    },
    setLast4SSN(value) {
      const str = String(value).replace(/\D/g, '').slice(0, 4)
      self.addTouched('SSN')
      self.SSNNumber1 = str[0] || ''
      self.SSNNumber2 = str[1] || ''
      self.SSNNumber3 = str[2] || ''
      self.SSNNumber4 = str[3] || ''
    },
    changeDOBString(newDOBString) {
      self.addTouched('DOBString')
      self.setDOB(userInputFormatDate(newDOBString))
    },
    setDOB(value) {
      self.DOBString = value
    },
    setSource(value) {
      self.source = value
    },
    changeGender(newGender) {
      self.addTouched('gender')
      self.gender = newGender
    },
    changeName(FML, newName) {
      self.addTouched(FML)
      self[FML] = newName
    },
    changeKey(key, value) {
      if (key === 'fullName') {
        self.addTouched('fullName')
        self.fullName = value
      }
    },
    setCourtFilter(value) {
      self.addTouched('courtFilter')
      self.courtFilter = value
    },
    changeTrafficTicketNumber(newTrafficTicketNumber) {
      self.addTouched('trafficTicketNumber')
      self.trafficTicketNumber = newTrafficTicketNumber.toUpperCase()
    },
    changeViolateDate(newViolateDate) {
      self.addTouched('violateDate')
      self.violateDate = userInputFormatDate(newViolateDate)
    },
    setUseNYDMVForm(value) {
      self.useNYDMVForm = !!value
    },
    // search status
    setSearchingTickets(status) {
      self.searchingTickets = status
    },
    // update which element is shown in each column
    setColumn(column, option) {
      // clear out ssn field if switching to ssn
      if (option === SEARCH_OPTIONS.lastFourSSN) {
        self.deleteLast4SSN('SSNNumber1')
        self.deleteLast4SSN('SSNNumber2')
        self.deleteLast4SSN('SSNNumber3')
        self.deleteLast4SSN('SSNNumber4')
      }
      self.resetTouched()
      self.resetDisplayErrors()
      self[column] = option
    },
    setIsNYSLicense(value) {
      self.resetTouched()
      self.resetDisplayErrors()
      self.isNYSLicense = !!value
      if (!self.isNYSLicense) {
        self.columnOne = SEARCH_OPTIONS.nameDOBGender
        self.columnTwo = SEARCH_OPTIONS.ticketNumber
        if (self.fetcherName === 'NY_STATE') {
          self.columnThree = SEARCH_OPTIONS.PlateNumberAndLicenseState
        } else {
          self.columnThree = null
        }
      } else {
        self.columnOne = self.fetcherName === 'NY_STATE' ? SEARCH_OPTIONS.LicenseIdAndDob : SEARCH_OPTIONS.NYSLicense
        self.columnTwo = SEARCH_OPTIONS.lastFourSSN
        if (self.fetcherName === 'NY_STATE') {
          self.columnThree = SEARCH_OPTIONS.DOBPlateNumberAndLicenseState
        } else {
          self.columnThree = null
        }
      }
    },
    toggleIsNYSLicense() {
      self.setIsNYSLicense(!self.isNYSLicense)
    },
    toggleSearchUsingOtherInfo() {
      self.searchUsingOtherInfo = !self.searchUsingOtherInfo
    },
    touchedSearchUsingOtherInfo() {
      self.searchUsingOtherInfoTouched = true
      // Cookies.set(TOUCHED_DROPDOWN_COOKIE_NAME, true, { expires: 7, path: '' })
    },
    setNassauSearchTab(tab) {
      self.resetTouched()
      self.resetDisplayErrors()
      self.nassauSearchTab = tab
    },
    getUserFormData() {
      const { user } = getRoot(self).auth
      if (user.objectId) {
        const keys = ['licenseID', 'gender', 'firstName', 'lastName']
        keys.forEach((key) => {
          if (user[key]) {
            self[key] = user[key]
          }
        })
        if (user.dob) {
          self.DOBString = user.dob.split('/').join(' / ')
        }
      }
    },
    setFetcherStatus(status) {
      self.fetchingTicketsStatus = status
    },
    setManualTrafficTicketError(error) {
      self.manualTrafficTicketError = error
    },
    resetSelectedTrafficTicket() {
      self.selectedTrafficTicket = null
      self.selectedTrafficTickets.clear()
    },
    // Keeps an attorney already picked for a ticket; only unpriced tickets get quotes[0].
    setSelectedTrafficTickets(tickets) {
      self.resetDisplayErrors()
      tickets.forEach((ticket) => {
        if (!ticket.fetcherTrafficTicket.quote) ticket.fetcherTrafficTicket.selectQuote(ticket.quotes[0])
      })
      self.selectedTrafficTickets = tickets.map((ticket) => ticket.ticketID)
      self.selectedTrafficTicket = tickets[0] || null
    },
    selectAllFightableTickets() {
      self.setSelectedTrafficTickets(self.fightableTickets)
    },
    toggleTicketSelection(ticket) {
      const isSelected = self.selectedTrafficTickets.includes(ticket)
      const next = self.fightableTickets.filter((item) =>
        item === ticket ? !isSelected : self.selectedTrafficTickets.includes(item),
      )
      self.setSelectedTrafficTickets(next)
    },
    setSelectedTrafficTicket(ticket) {
      self.resetDisplayErrors()
      self.selectedTrafficTicket = ticket
      if (ticket) {
        self.selectedTrafficTicket.fetcherTrafficTicket.selectQuote(
          self.selectedTrafficTicket.quotes[0],
        )
      }
    },
    showErrorToast(message) {
      const all = getParent(self)
      all.ui.toasts.addToast({
        message:
          message ||
          `Sorry something unexpected happened. Please try again or contact us for further support.`,
        type: 'ERROR',
      })
    },
    setSubmittingManualTicket(isSubmitting) {
      self.isSubmittingManualTicket = isSubmitting
    },
    // `origin` tags where in the lookup the upload came from. Every fetcher
    // shares these tags, so the route is matched on the shared `/no-result`
    // suffix rather than on a per-state path.
    resolveManualSubmitOrigin() {
      if (self.fetcherName === 'NY_CRIMINAL') return 'webapp-lookup-criminal-manual_submission'
      const route = Router.route || ''
      if (route.includes('/no-result')) return 'webapp-lookup-pct'
      if (self.fetchingTicketsStatus === REQUEST_STATUSES.loading)
        return 'webapp-lookup-loading_upload'
      return 'webapp-lookup-manual_submission'
    },
    // Signs the user in so the ticket is created against a real account.
    // Returns false (and sets the form error) when we could not get there.
    ensureAccountForManualSubmit: flow(function* ensureAccountForManualSubmitGen() {
      const { auth, ui } = getRoot(self)
      if (auth.isLoggedIn) return true

      // Not connected: create a user from the lookup form (random password).
      const password = StringTools.random(15)
      auth.user.changeKey('email', self.email)
      auth.user.changeKey('username', self.email)
      auth.user.changeKey('name', self.fullName)
      auth.user.changeKey('phoneNumber', self.phoneNumber)
      auth.user.changeKey('password', password)
      auth.user.changeKey('paymentProcessor', { type: 'braintree' })
      const USER_KEYS_TO_SAVE = [
        'email',
        'username',
        'name',
        'phoneNumber',
        'password',
        'paymentProcessor',
      ]
      const createdUser = yield auth.user.createNewUser({
        keys: [...USER_KEYS_TO_SAVE],
        locationState: self.fetcherState,
      })

      if (!createdUser) {
        // The account already exists: ask the user to sign in rather than
        // letting them know an account was created.
        const userAlreadyRegistered = auth.user.displayErrors.some(
          (displayError) => displayError.message === 'This email is already registered.',
        )
        if (userAlreadyRegistered) {
          auth.changeKey('email', self.email) // Populate the user's email in the sign-in form
          auth.user.changeKey('password', '') // Avoid prepopulating the sign-up form with the random password we generated earlier
          ui.setLoginModalShowing(true) // TODO: show the correct modal depending on the user
          TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, 'login_required')
        } else {
          self.manualTrafficTicketError = 'An error occurred. Please try again later.'
          TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, 'user_create')
        }
        return false
      }

      auth.changeKey('email', self.email)
      auth.changeKey('password', password)
      // `login` swallows its own errors and reports failure through its return
      // value, so creating the ticket regardless would POST it with no session.
      const signedIn = yield auth.login()
      if (!signedIn) {
        self.manualTrafficTicketError =
          'We created your account but could not sign you in. Please log in and submit again.'
        TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, 'login_failed')
        return false
      }
      return true
    }),
    // The ticket is already created by the time this runs, so a failed upload
    // must not strand the user on the form — an agent can still follow up on
    // the lead. Report it instead of letting the rejection go unhandled.
    uploadManualTicketImage: flow(function* uploadManualTicketImageGen() {
      try {
        yield self.manualTrafficTicket.uploadTicketImage()
        return true
      } catch (err) {
        console.error('manual ticket image upload failed', err)
        TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, 'image_upload')
        return false
      }
    }),
    submitManualTicket: flow(function* createTicketWithImage() {
      // `createTicket` always POSTs a new record, so a double submit would
      // create duplicate tickets.
      if (self.isSubmittingManualTicket) return false
      self.manualTrafficTicketError = ''
      if (!self.allowCreateManualTrafficTicket()) {
        self.manualTrafficTicketError = 'At least one of the information above must be entered.'
        TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, 'validation')
        return false
      }
      TRACKING.trafficLookup.manualSubmit(self.fetcherName)

      const MANUAL_TRAFFIC_TICKET_KEYS_TO_SAVE = ['ticketID', 'comment', 'locationState']
      const origin = self.resolveManualSubmitOrigin()
      // The VA region is a lookup-form grouping, not a TrafficRegion record, so
      // it travels as telemetry rather than as a `trafficRegion` pointer.
      const extraTelemetry = self.courtFilter ? { lookup_region: self.courtFilter } : null

      self.setSubmittingManualTicket(true)
      try {
        const hasAccount = yield self.ensureAccountForManualSubmit()
        if (!hasAccount) return false

        self.manualTrafficTicket.setState(self.fetcherState)
        yield self.manualTrafficTicket.createTicket(
          MANUAL_TRAFFIC_TICKET_KEYS_TO_SAVE,
          origin,
          self.fetcherName,
          extraTelemetry,
          TrafficTicketService.SIGNUP_SOURCES.lookup,
        )
        yield self.uploadManualTicketImage()
        TRACKING.trafficLookup.manualSubmitted(self.fetcherName)
        // Stop late fetcher redirects from yanking the user off /submitted.
        self.setFetcherStatus(REQUEST_STATUSES.complete)
        self.clearFlowSession()
        Router.push(lookupPath('submitted'))
        return true
      } catch (err) {
        // `parseError` rejects with a string, but the user and login stores can
        // throw an Error, and the field only holds a string.
        self.manualTrafficTicketError =
          typeof err === 'string' ? err : err?.message || 'An error occurred. Please try again later.'
        TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, err)
        return false
      } finally {
        self.setSubmittingManualTicket(false)
      }
    }),
    switchPaymentProcessor: flow(function* switchPaymentProcessor() {
      self.validateKeyList(['selectedTrafficTicket'])
      const hasError = self.validateKey('selectedTrafficTicket')
      if (hasError) return false

      const root = getRoot(self)
      const { user } = root.auth
      if (user) {
        const { locationState } = self.selectedTrafficTicket.fetcherTrafficTicket
        yield overwritePaymentProcessor(user, locationState).then((res) => {
          if (res.isAffiliatedWithWinitLaw) {
            user.changeKey('paymentProcessor', { type: PAYMENT_PROVIDERS.cko })
          }
        })
      }
    }),
    routeToAttorneySelectorOrCheckout() {
      self.resetTouched()
      self.resetDisplayErrors()
      self.validateKeyList(['selectedTrafficTicket'])
      const hasError = self.validateKey('selectedTrafficTicket')
      if (hasError) return false
      TRACKING.trafficLookup.checkout(
        self.fetcherName,
        self.selectedTrafficTicket?.fetcherTrafficTicket?.winItPrice ||
          self.selectedTrafficTicket?.fetcherTrafficTicket?.quote?.pricing?.winItPrice
      )
      if (!self.renderAttorneySelector) {
        TRACKING.traffic.fightMyTicket('checkout')
        return Router.push(lookupPath('checkout'))
      }
      TRACKING.traffic.fightMyTicket('attorney')
      return Router.push(lookupPath('attorney'))
    },
    previousStep() {
      Router.push(lookupPath('results'))
    },
    nextStep() {
      Router.push(lookupPath('checkout'))
    },
    writePropertiesOnUser() {
      const keys = ['gender', 'licenseID', 'SSN']
      const safe = !self.validateKeyList(keys)
      if (safe) {
        const { user } = getRoot(self).auth
        keys.forEach((key) => {
          user.changeKey(key, self[key])
        })
        if (typeof self.licenseState === 'object') {
          user.changeKey('licenceState', self.licenseState.shortName)
        } else {
          user.changeKey('licenceState', self.licenseState)
        }
        user.changeKey('last4ssn', self.SSN)
        user.changeKey('LastFour', self.SSN)
        user.changeKey('dob', self.DOBString)
        user.changeKey('name', self.fullName)
        user.changeKey('email', self.email)
        user.changeKey('username', self.email)
        user.changeKey('phoneNumber', self.phoneNumber)
      }
    },
    addIdentifiersToTickets(result, preserveSelection = false) {
      const previouslySelectedId =
        preserveSelection && self.selectedTrafficTicket
          ? self.selectedTrafficTicket.ticketID
          : null
      const ticketsWithIdentifiers = result.filter((ticket) => ticket.fetcherTrafficTicket).map((ticket) => {
        if (ticket.fetcherTrafficTicket.fetcherName === 'NY_CRIMINAL') {
          ticket.fetcherTrafficTicket.isCriminalFetcher = true
        }
        // add ticketID to ticket
        ticket.ticketID = ticket.fetcherTrafficTicket.ticketID
        if (ticket.fetcherTrafficTicket.fetcherName === 'NY_CRIMINAL' || ticket.fetcherTrafficTicket.fetcherName === 'NY_STATE') {
          ticket.charges = ticket.fetcherTrafficTicket.charges
          ticket.defendant = ticket.fetcherTrafficTicket.name
          ticket.hearingDate = ticket.fetcherTrafficTicket.hearingDate
        }
        // add id to quotes
        ticket.quotes =
          ticket.quotes &&
          ticket.quotes.map((quote) => {
            quote.id = ticket.ticketID + quote.objectId
            return quote
          })
        return ticket
      })
      self.fetcherTickets = ticketsWithIdentifiers

      self.fetcherTickets.forEach((fetcherTicket, i) => {
        fetcherTicket.fetcherTrafficTicket.setDefendant(ticketsWithIdentifiers[i].defendant)
      })

      self.resetSelectedTrafficTicket()
      if (previouslySelectedId) {
        // A late VA batch must not yank the row the user just picked out from
        // under them; fall through to the default only if it is gone.
        const stillPresent = self.fetcherTickets.find(
          (ticket) => ticket.ticketID === previouslySelectedId,
        )
        if (stillPresent) {
          // Assign directly rather than via setSelectedTrafficTicket(), which
          // force-selects quotes[0] and would revert an attorney the user has
          // already picked on the results page (same reason as restoreFlowState).
          self.selectedTrafficTicket = stillPresent
          self.attachPendingManualUploadToResults()
          return
        }
      }
      self.selectFirstTicketByDefault()
      self.attachPendingManualUploadToResults()
    },
    // If the user attached a ticket photo while the fetcher was still running,
    // copy it onto the first fightable (or first) result so search + upload merge.
    attachPendingManualUploadToResults() {
      const pending = self.manualTrafficTicket
      if (
        !pending ||
        !pending.persistedTicketImage ||
        !pending.persistedTicketImage.isImageAttached
      ) {
        return
      }
      const target =
        self.findFirstFightableTicket() ||
        (self.fetcherTickets.length > 0 ? self.fetcherTickets[0] : null)
      if (!target || !target.fetcherTrafficTicket) return
      if (target.fetcherTrafficTicket.persistedTicketImage?.isImageAttached) return
      target.fetcherTrafficTicket.changeKey(
        'persistedTicketImage',
        getSnapshot(pending.persistedTicketImage),
      )
    },
    isOnManualSubmittedPage() {
      const pathname = Router.pathname || Router.route || ''
      return pathname.includes('/submitted')
    },
    selectFirstTicketByDefault() {
      const ticketToSelect = self.findFirstFightableTicket()
      if (ticketToSelect) {
        self.setSelectedTrafficTicket(ticketToSelect)
      }
    },
    findFirstFightableTicket() {
      for (let i = 0; i < self.fetcherTickets.length; i += 1) {
        const ticket = self.fetcherTickets[i]
        if (ticket.isFightable) {
          return ticket
        }
      }
      return null
    },
    getFetcherTicketsFromId: flow(function* getFetcherTicketsFromIdGen(searchObjectId) {
      try {
        const result =
          yield TrafficTicketService.getFetcherTicketsBySearchCriteriaId(searchObjectId)
        if (result.length === 0) {
          throw new Error('No results found.')
        }
        self.addIdentifiersToTickets(result)
      } catch (error) {
        // self.showToast(error, 'ERROR')
        console.error('error fetching tickets: ', error)
      }
    }),
    canDisplayBackButton() {
      return false
    },
    canDisplayNextButton() {
      return true
    },
    showNoResultPage() {
      // User already finished a manual upload during the search — don't yank them.
      if (self.isOnManualSubmittedPage()) return
      if (self.fetcherName === 'NY') {
        Router.push(lookupPath('no-result'))
      } else {
        const slugFromMap = Object.entries(FETCHER_CITY_STATES.traffic).find(
          ([, config]) => config.fetcherName === self.fetcherName,
        )?.[0]
        const pathName =
          slugFromMap ||
          FETCHER_NAMES_TITLES[self.fetcherName].toLowerCase().replace(/\s+/g, '-')
        Router.push(`/traffic/lookup/[state]/no-result`, `/traffic/lookup/${pathName}/no-result`, {
          shallow: true,
        }) // We need to make sure that the shallow routing is used in order to keep data on this new page
      }
    },
    getFetcherTickets: flow(function* (flowType) {
      self.setSource('fetcher')

      const validationKeys =
        flowType === 'parking'
          ? [
              ...SEARCH_OPTIONS_VALIDATION_KEYS[self.columnOne],
              ...SEARCH_OPTIONS_VALIDATION_KEYS[self.columnTwo],
            ]
          : undefined

      yield self.validateForm(validationKeys)

      if (self.hasDisplayErrors) return false

      if (flowType !== 'parking') {
        TRACKING.trafficLookup.search(self.fetcherName)
      }

      const isNYDMVFlow = self.useNYDMVForm === true
      // Fire "information added" conversions:
      // - For legacy/other flows: always here (preserves existing behavior).
      // - For NY DMV flow: only if Step 1 hasn't already sent them.
      if (!isNYDMVFlow || !self.hasSentFetcherInfoAdded) {
        TRACKING.traffic.fetcherInfoAdded(self.fetcherName, self.ga4State)
        if (isNYDMVFlow) {
          self.markFetcherInfoAdded()
        }
      }

      // Google analytics timer (search latency)
      const searchTimer = TRACKING.traffic.fetcherResultsTiming(
        'Lookup informations added',
        self.fetcherName,
      )
      searchTimer.start()

      // Timeout for fetcher request
      const timeoutEnabled = process.env.ENABLE_FETCHER_TIMEOUT_DURATION === 'true'
      let isTimedOut = false

      const timeoutDuration = getTrafficFetcherTimeout(
        self.fetcherName,
        !!self.courtFilter,
      )
      const timer = setTimeout(() => {
        isTimedOut = true
        if (timeoutEnabled) {
          console.info('Fetcher response took too long => pushing to no-result page.')
          self.showNoResultPage()
        }
      }, timeoutDuration)

      try {
        self.setFetcherStatus(REQUEST_STATUSES.loading)
        Router.prefetch(lookupPath('results'))
        const url = process.env.PARSE_SERV_URL
        

        const searchCriteria = self.searchData
        if (flowType === 'parking')
          searchCriteria.telemetry = {
            origin_create: 'v2 web app parking pay flow',
            created_source: 'regular_flow',
            lookup_page: 'parking-lookup',
            fetcher_name: self.fetcherName,
          }
        else
          searchCriteria.telemetry = {
            origin_create: 'v2 web app traffic lookup flow',
            created_source: 'regular_flow',
            lookup_page: 'traffic-lookup',
            fetcher_name: self.fetcherName,
          }
        // VA with a region selected: split courts into small batches to avoid
        // overloading the scraper (rate limiting) while keeping each request fast.
        // The first batch runs immediately and each later batch is released 5s
        // after the one before it, so the scraper is never hit with a burst.
        const vaCourts = searchCriteria.courts
        if (self.fetcherName === 'VA' && vaCourts && vaCourts.length > 1) {
          const BATCH_SIZE = 3
          const VA_BATCH_STAGGER_MS = 5000
          const batches = []
          for (let i = 0; i < vaCourts.length; i += BATCH_SIZE) {
            batches.push(vaCourts.slice(i, i + BATCH_SIZE))
          }
          let allResults = []
          let completed = 0
          let hasRedirected = false
          const total = batches.length

          const startBatchLookup = (batch) => {
            const batchCriteria = { ...searchCriteria, courts: batch }
            const batchSocket = io(url, { transports: ['websocket'] })

            batchSocket.on('connect', () => {
              batchSocket.emit('getFetcherTickets', batchCriteria)

              batchSocket.on('getFetcherTicketsError', () => {
                console.error('VA court batch lookup failed for:', batch)
                completed += 1
                if (completed === total) {
                  clearTimeout(timer)
                  if (allResults.length > 0) {
                    const alreadyReported = hasRedirected
                    self.saveUserInfo()
                    self.addIdentifiersToTickets(allResults, alreadyReported)
                    if (!alreadyReported) {
                      hasRedirected = true
                      Router.push(lookupPath('results'))
                      // Only report the conversion for a search that has not
                      // already reported one from an earlier batch.
                      TRACKING.traffic.fetcherResults(self.fetcherName, self.ga4State)
                      TRACKING.trafficLookup.results(self.fetcherName, allResults.length)
                    }
                    searchTimer.finish()
                    Delays.msDelayPromise(1500).then(() => {
                      self.setFetcherStatus(REQUEST_STATUSES.complete)
                    })
                  } else {
                    TRACKING.traffic.fetcherNoResults(self.fetcherName, self.ga4State)
                    TRACKING.trafficLookup.noResult(self.fetcherName)
                    self.setFetcherStatus(REQUEST_STATUSES.error)
                    self.showNoResultPage()
                  }
                }
                try { batchSocket.close() } catch (_) {}
              })

              batchSocket.on('getFetcherTicketsResults', (result) => {
                if (Array.isArray(result) && result.length > 0) {
                  allResults = allResults.concat(result)
                  // Show results as soon as we get the first batch
                  if (!hasRedirected) {
                    hasRedirected = true
                    clearTimeout(timer)
                    self.saveUserInfo()
                    self.addIdentifiersToTickets(allResults)
                    const pathname = Router.pathname || Router.route || ''
                    const stillInLookupFlow =
                      pathname.includes('/traffic/lookup') &&
                      !pathname.includes('/submitted')
                    if (stillInLookupFlow) {
                      Router.push(lookupPath('results'))
                    }
                    TRACKING.traffic.fetcherResults(self.fetcherName, self.ga4State)
                    TRACKING.trafficLookup.results(self.fetcherName, allResults.length)
                  } else {
                    // addIdentifiersToTickets replaces fetcherTickets wholesale,
                    // so re-send every batch collected so far, not just this one,
                    // keeping whatever row the user has already selected.
                    self.addIdentifiersToTickets(allResults, true)
                  }
                }
                completed += 1
                if (completed === total) {
                  clearTimeout(timer)
                  searchTimer.finish()
                  if (allResults.length > 0) {
                    Delays.msDelayPromise(1500).then(() => {
                      self.setFetcherStatus(REQUEST_STATUSES.complete)
                    })
                  } else {
                    TRACKING.traffic.fetcherNoResults(self.fetcherName, self.ga4State)
                    TRACKING.trafficLookup.noResult(self.fetcherName)
                    self.setFetcherStatus(REQUEST_STATUSES.error)
                    self.showNoResultPage()
                  }
                }
                try { batchSocket.close() } catch (_) {}
              })
            })
          }

          // Ramp the batches in one at a time rather than releasing every
          // remaining batch at once — the VA courts site rate-limits bursts,
          // and a throttled batch costs a 30s backoff on the scraper side.
          // The ramp spans ~25s for the largest region, so a batch can still be
          // pending after the user has walked away: drop the rest once they
          // leave the lookup flow rather than opening sockets they never see.
          batches.forEach((batch, index) => {
            if (index === 0) {
              startBatchLookup(batch)
              return
            }
            setTimeout(() => {
              const currentPath = Router.pathname || Router.route || ''
              if (!currentPath.includes('/traffic/lookup')) {
                // Still count a dropped batch. `completed` is only ever
                // incremented by the socket handlers, so bailing without it
                // would leave `completed === total` unreachable: the timeout
                // timer would never be cleared and would later push the user to
                // the no-result page from wherever they had moved to, and the
                // fetcher status would stay `loading` forever.
                completed += 1
                if (completed === total) {
                  clearTimeout(timer)
                  searchTimer.finish()
                  // No navigation here — the user has left the flow.
                  self.setFetcherStatus(
                    allResults.length > 0
                      ? REQUEST_STATUSES.complete
                      : REQUEST_STATUSES.init,
                  )
                }
                return
              }
              startBatchLookup(batch)
            }, index * VA_BATCH_STAGGER_MS)
          })
        } else {
        // Default: single request (every non-VA flow)
        const socket = io(url, {
          transports: ['websocket'],
        })
        socket.on('connect', () => {
          socket.emit('getFetcherTickets', searchCriteria)
          socket.on('getFetcherTicketsError', (error) => {
            clearTimeout(timer)
            TRACKING.traffic.fetcherNoResults(self.fetcherName, self.ga4State)
            TRACKING.trafficLookup.noResult(self.fetcherName)
            self.setFetcherStatus(REQUEST_STATUSES.error)
            console.error('error fetching tickets: ', error)
            self.showNoResultPage()
          })

          socket.on('triggerFetcherTicketsConv', () => {
            TRACKING.traffic.fetcherResults(self.fetcherName, self.ga4State)
            TRACKING.trafficLookup.results(
              self.fetcherName,
              self.fetcherTickets.length
            )
          })

          socket.on('getFetcherTicketsResults', (result) => {
            clearTimeout(timer)
            const pathname = Router.pathname || Router.route || ''
            const stillInLookupFlow =
              pathname.includes('/traffic/lookup') && !pathname.includes('/submitted')
            let shouldRedirectToResults =
              stillInLookupFlow &&
              (self.fetchingTicketsStatus === REQUEST_STATUSES.loading ||
                pathname === lookupPath('results', pathname))
            if (
              self.fetchingTicketsStatus === REQUEST_STATUSES.error ||
              self.fetchingTicketsStatus === REQUEST_STATUSES.complete
            )
              shouldRedirectToResults = false
            try {
              self.saveUserInfo()
              if (result.length === 0) {
                throw new Error('No results found.')
              }
              self.addIdentifiersToTickets(result)
              if (shouldRedirectToResults) {
                Router.push(lookupPath('results'))
              }
              if (!IS_FETCHER_COMBINED[self.fetcherName]) {
                TRACKING.traffic.fetcherResults(self.fetcherName, self.ga4State)
                TRACKING.trafficLookup.results(self.fetcherName, result.length)
              }
              searchTimer.finish()
              Delays.msDelayPromise(1500).then(() => {
                self.setFetcherStatus(REQUEST_STATUSES.complete)
              })
            } catch (error) {
              TRACKING.traffic.fetcherNoResults(self.fetcherName, self.ga4State)
              TRACKING.trafficLookup.noResult(self.fetcherName)
              self.setFetcherStatus(REQUEST_STATUSES.error)
              console.error('error fetching tickets: ', error)
              if (stillInLookupFlow && ((timeoutEnabled && !isTimedOut) || !timeoutEnabled)) {
                self.showNoResultPage()
              }
            }
          })
        })
        }
      } catch (error) {
        clearTimeout(timer)
        TRACKING.traffic.fetcherNoResults(self.fetcherName, self.ga4State)
        TRACKING.trafficLookup.noResult(self.fetcherName)
        self.setFetcherStatus(REQUEST_STATUSES.error)
        console.error('error fetching tickets: ', error)
        self.showNoResultPage()
      }
    }),
    SaveOrUpdateTicket: flow(function* handleSaveOrUpdate(ticket = self.selectedTrafficTicket) {
      try {
        let savedTicket
        const sessionToken = getUserSessionToken(self)
        if (ticket.fetcherTrafficTicket.objectId) {
          if (TRAFFIC_STATUS_ORDER[ticket.fetcherTrafficTicket.status] < 2) {
            ticket.fetcherTrafficTicket.status = TRAFFIC_STATUS.pendingConf
          }
          savedTicket = yield ticket.fetcherTrafficTicket.updateTicket(
            TRAFFIC_TICKET_KEYS_TO_SAVE,
            sessionToken,
          )
        } else {
          ticket.fetcherTrafficTicket.status = TRAFFIC_STATUS.pendingConf
          const rootStore = getRoot(self)
          const { fetcherName } = rootStore.trafficLookup
          const keysForTicketCreation = TRAFFIC_TICKET_KEYS_TO_SAVE
          const keysForTicketCreationFromResult = ['attorney', 'searchResultId']
          // For now (26.10.21) we want to create ticket from search result info just for the NJ Lookup Fetcher.
          // This will be extended to other states once it's fully working for NJ
          if (fetcherName === 'NJ') {
            savedTicket =
              yield ticket.fetcherTrafficTicket.createTicketFromSearchResult(
                keysForTicketCreationFromResult,
                'fetcher',
                fetcherName,
                TrafficTicketService.SIGNUP_SOURCES.lookup,
              )
          } else {
            savedTicket = yield ticket.fetcherTrafficTicket.createTicket(
              keysForTicketCreation,
              'fetcher',
              fetcherName,
              null,
              TrafficTicketService.SIGNUP_SOURCES.lookup,
            )
          }
        }
        return savedTicket
      } catch (error) {
        self.showToast(error, 'ERROR')
        console.error('error saving or updating ticket', error)
      }
    }),
    chargeTicket: flow(function* handleChargeTicket(
      nonce,
      ticket = self.selectedTrafficTicket,
      { notify = true } = {},
    ) {
      const root = getRoot(self)
      const { user } = root.auth
      try {
        const sessionToken = getUserSessionToken(self)
        const { ticketID } = ticket.fetcherTrafficTicket

        const result = yield chargeTrafficTicket(null, ticketID, sessionToken)
        if (result && !result.includes('Charge success for ticket')) {
          throw new Error('Issue charging ticket')
        }
        const ticketObjectId = ticket?.fetcherTrafficTicket?.objectId
        const purchaseValue = ticket?.fetcherTrafficTicket?.winItPrice
        if (ticketObjectId) trackMetaBrowserPurchase(ticketObjectId, purchaseValue)
        if (notify) self.showToast('Ticket Submitted', 'SUCCESS')
        return result
      } catch (error) {
        if (
          [PAYMENT_PROVIDERS.cko, PAYMENT_PROVIDERS.evervault].includes(user.paymentProcessor.type)
        ) {
          getRoot(self).payment.remountFlow()
        }
        console.error('error charging ticket', error)
        self.showToast(error, 'ERROR')
        TRACKING.trafficLookup.unpaid(self.fetcherName, error)
        return false
      }
    }),
    handleCheckout: flow(function* handleCheckoutGen(nonce) {
      Router.prefetch(lookupPath('success'))
      TRACKING.trafficLookup.payAttempt()
      const savedOrUpdatedTicket = yield self.SaveOrUpdateTicket()
      let chargedTicket
      if (savedOrUpdatedTicket) {
        chargedTicket = yield self.chargeTicket(nonce)
        if (!chargedTicket) {
          return false
        }
        // should be in processing now.
        self.selectedTrafficTicket.fetcherTrafficTicket.setStatus(TRAFFIC_STATUS.processing)
      } else {
        return false
      }
      // User fields update
      self.saveUserInfo()

      TRACKING.trafficLookup.paid(self.fetcherName)
      // Real USD charged for the fetcher flow — same amount Meta's purchase
      // event uses in chargeTicket. Without it the GA4 conversion downgrades to
      // generate_lead and this flow contributes no Revenue/LTV.
      TRACKING.traffic.submissionConversion(
        self.selectedTrafficTicket.fetcherTrafficTicket.objectId,
        'fetcher',
        self.ga4State,
        self.selectedTrafficTicket?.fetcherTrafficTicket?.winItPrice ||
          self.selectedTrafficTicket?.fetcherTrafficTicket?.quote?.pricing?.winItPrice
      )
      self.clearFlowSession()
      Router.push(lookupPath('success'))
      yield Delays.msDelayPromise(3000)
    }),
    // Docket checkout: one card, one charge per ticket. Tickets already in
    // processing were charged by an earlier attempt, so a retry after a failed
    // charge only bills the rest.
    handleCheckoutAll: flow(function* handleCheckoutAllGen(nonce) {
      if (self.checkoutTickets.length <= 1) return yield self.handleCheckout(nonce)
      Router.prefetch(lookupPath('success'))
      TRACKING.trafficLookup.payAttempt()
      const pending = self.checkoutTickets.filter(
        (ticket) => ticket.fetcherTrafficTicket.status !== TRAFFIC_STATUS.processing,
      )
      for (let i = 0; i < pending.length; i += 1) {
        const ticket = pending[i]
        const saved = yield self.SaveOrUpdateTicket(ticket)
        if (!saved) return false
        const charged = yield self.chargeTicket(nonce, ticket, { notify: false })
        if (!charged) return false
        ticket.fetcherTrafficTicket.setStatus(TRAFFIC_STATUS.processing)
        TRACKING.traffic.submissionConversion(
          ticket.fetcherTrafficTicket.objectId,
          'fetcher',
          self.ga4State,
          ticket.fetcherTrafficTicket.winItPrice || ticket.fetcherTrafficTicket.quote?.pricing?.winItPrice,
        )
      }
      self.saveUserInfo()
      TRACKING.trafficLookup.paid(self.fetcherName)
      self.showToast(`${self.checkoutTickets.length} Tickets Submitted`, 'SUCCESS')
      self.clearFlowSession()
      Router.push(lookupPath('success'))
      yield Delays.msDelayPromise(3000)
      return true
    }),
    unsubscribeContactInfo: flow(function* unsubContactInfo(objectId) {
      try {
        yield unsubscribeByFetcherSearchContactInfo(objectId)
        return true
      } catch (error) {
        console.error('Error unsubscribing fetcher search contact info', error)
        return false
      }
    }),
    showToast(message, type) {
      const all = getRoot(self)
      all.ui.toasts.addToast({
        message,
        type,
      })
    },
    saveUserInfo() {
      const { user } = getRoot(self).auth
      self.writePropertiesOnUser()
      const keys = [
        'name',
        'email',
        'username',
        'phoneNumber',
        'gender',
        'licenseID',
        'licenseState',
        'last4ssn',
        'LastFour',
        'dob',
        'paymentProcessor',
      ]
      user.updateUser(keys)
      if (process.browser) {
        self.persistLookupFormToStorage()
      }
    },
  }))

const CustomValidation = buildCustomValidationStore({
  validationObject: FETCHER_VALIDATIONS,
  asyncValidationObject: FETCHER_ASYNC_VALIDATIONS,
})

const FetcherModel = types.compose(TrafficLookupStore, CustomValidation)

export default FetcherModel
